import React from 'react'

/**
 * CallCenterHeader Component
 * Canonical Top Canopy matching the Attendance module design language.
 * Displays module eyebrow, 3xl bold workstation title, contextual subtitle, and right-aligned SIP device toggle.
 */
export default function CallCenterHeader({
  isSipDeviceOn,
  isSipToggling,
  sipError,
  onSipToggle,
  isAudioPrimed,
  onTestRingtone,
}) {
  return (
    <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
          Telephony &amp; Voice
        </p>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
          Call Centre &amp; Softphone Workstation
        </h1>
        <p className="mt-0.5 text-xs text-on-surface-variant font-medium">
          Place outbound WebRTC calls, manage team callbacks, and monitor live agent queue presence.
        </p>
      </div>

      {/* ── SIP Device Toggle Switch ── */}
      <div className="flex items-center gap-3">
        {sipError && (
          <span
            className="text-xs text-red-600 font-semibold max-w-xs truncate"
            title={sipError}
          >
            ⚠ {sipError}
          </span>
        )}
        <div className="flex items-center gap-3 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest px-4 py-2 shadow-xs">
          <span className="text-xs font-bold text-on-surface">SIP Device:</span>
          <button
            type="button"
            disabled={isSipToggling}
            onClick={onSipToggle}
            aria-label="Toggle SIP Device"
            className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out flex items-center cursor-pointer ${
              isSipToggling
                ? 'opacity-50 cursor-wait bg-amber-400'
                : isSipDeviceOn
                  ? 'bg-primary justify-end'
                  : 'bg-slate-300 justify-start'
            }`}
          >
            <span className="w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform" />
          </button>
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-xl transition-colors ${
              isSipToggling
                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                : isSipDeviceOn
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                  : 'bg-surface-container-high text-on-surface-variant border border-outline-variant/30'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isSipToggling
                  ? 'bg-amber-500 animate-pulse'
                  : isSipDeviceOn
                    ? 'bg-emerald-500 animate-pulse'
                    : 'bg-slate-400'
              }`}
            />
            {isSipToggling
              ? 'Connecting...'
              : isSipDeviceOn
                ? 'IP Calling (Online)'
                : 'PSTN Fallback (Offline)'}
          </span>
        </div>
        {/* ── Test Sound & Audio Priming Button ── */}
        {onTestRingtone && (
          <button
            type="button"
            onClick={onTestRingtone}
            title="Click to test ringtone and unlock browser audio permissions"
            className={`flex items-center gap-1.5 rounded-2xl border px-3.5 py-2 text-xs font-bold transition-all shadow-xs cursor-pointer ${
              isAudioPrimed
                ? 'border-outline-variant/30 bg-surface-container-lowest text-on-surface hover:bg-surface-container-low'
                : 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 animate-pulse'
            }`}
          >
            <span>🔔</span>
            <span>{isAudioPrimed ? 'Test Sound' : 'Enable Sound'}</span>
          </button>
        )}
      </div>
    </section>
  )
}
